import parse, { domToReact, Element } from 'html-react-parser'
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser'
import { Link } from '@tanstack/react-router'
import { getConfluencePageId, sanitizeHtml } from '@/utils/utils.ts'

interface PolicyBodyProps {
    html?: string
}

/**
 * Render sanitized policy body HTML, rewriting in-body links that point at other
 * Confluence policy pages into TanStack Router <Link>s so they navigate within
 * the SPA (with prefetch/accessibility) instead of forcing a full page load.
 *
 * Manually authored links can opt out of this rewriting by appending a
 * `?directtoconfluence` marker to the href in Confluence. Those point at
 * restricted/auth-required pages that aren't viewable in the app, so they are
 * left as direct Confluence links that open in a new tab.
 */
export default function PolicyBody({ html }: PolicyBodyProps) {
    const clean = sanitizeHtml(html).__html;

    const options: HTMLReactParserOptions = {
        replace(domNode) {
            if (!(domNode instanceof Element) || domNode.name !== 'a') {
                return
            }

            const href = domNode.attribs.href
            if (!href) return

            const className = domNode.attribs.class ?? ''
            const isEmbeddedFile = className.split(/\s+/).includes('confluence-embedded-file')

            const resolvedHref = isEmbeddedFile && href.startsWith('/wiki/download')
                ? `https://ptsem.atlassian.net${href}`
                : href;

            if (isEmbeddedFile) {
                return (
                    <a href={resolvedHref} rel="noopener noreferrer">
                        {domToReact(domNode.children as DOMNode[], options)}
                    </a>
                )
            }

            const url = new URL(href, window.location.origin)

            // Explicit opt-out: keep the link pointing at Confluence and open it
            // in a new tab. Checked before the page-ID conversion because these
            // URLs still contain a /pages/<id>/ segment that would otherwise be
            // rewritten into an in-app link.
            if (url.searchParams.has('directtoconfluence')) {
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer nofollow">
                        {domToReact(domNode.children as DOMNode[], options)}
                    </a>
                )
            }

            const pageId = getConfluencePageId(href)
            if (!pageId) return

            return (
                <Link to="/policy/$pageId" params={{ pageId }}>
                    {domToReact(domNode.children as DOMNode[], options)}
                </Link>
            )
        },
    }

    return <div className="main">{parse(clean, options)}</div>
}
